import React from "react";
import { FieldErrors } from "react-hook-form";
import {
  CheckCircle,
  ImageIcon,
  Monitor,
  RefreshCw,
  Smartphone,
  UploadCloud,
  X,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Label } from "components/ui/label";
import { Button } from "components/ui/button";
import { BannerAspect, type CampaignType } from "@/@types/gamification";
import {
  CUSTOMER_FACING_COPY,
  FILE_MESSAGES,
  UPLOAD_COPY,
} from "../../constants/messages";
import { FileUploadButton } from "../../components/file-upload-button";
import { BannerGalleryPicker } from "../../components/banner-gallery-picker";
import type { BannerSlotState } from "../../hooks/use-banner-slot";
import { type UpdateGamificationFormData } from "../../validations/gamification.validation";
import { MAX_UPLOAD_MB } from "../../constants/upload";

interface BannersCardProps {
  errors: FieldErrors<UpdateGamificationFormData>;
  cover: BannerSlotState;
  alternative: BannerSlotState;
  hasBanner3x4: boolean;
  hasBanner16x9: boolean;
  campaignType?: CampaignType;
}

const IMAGE_ACCEPT = "image/*";

const EXISTING_CLASS =
  "group relative h-auto w-full flex-col gap-2 whitespace-normal rounded-none border-0 bg-green-50/60 text-center font-normal text-green-800 transition-colors hover:bg-green-50 hover:text-green-800";

const UPLOAD_CLASS =
  "group h-auto w-full flex-col gap-2 whitespace-normal rounded-none border-2 border-dashed border-gray-300 bg-white text-center font-normal text-gray-600 transition-colors hover:border-primary hover:bg-primary/5 hover:text-gray-600";

type BannerVariant = "mobile" | "desktop";

interface BannerSlotConfig {
  id: string;
  label: string;
  ratioLabel: string;
  variant: BannerVariant;
  galleryAspect: BannerAspect;
  aspect: string;
  frameWidth: string;
  slot: BannerSlotState;
  hasExisting: boolean;
  campaignType?: CampaignType;
  error?: string;
}

const VARIANT_ICON: Record<BannerVariant, React.ReactNode> = {
  mobile: <Smartphone className="h-4 w-4" />,
  desktop: <Monitor className="h-4 w-4" />,
};

const CHECKERBOARD_STYLE: React.CSSProperties = {
  backgroundColor: "#f8fafc",
  backgroundImage:
    "linear-gradient(45deg, #e2e8f0 25%, transparent 25%), linear-gradient(-45deg, #e2e8f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e2e8f0 75%), linear-gradient(-45deg, transparent 75%, #e2e8f0 75%)",
  backgroundSize: "16px 16px",
  backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0",
};

const FrameChrome: React.FC<{ variant: BannerVariant }> = ({ variant }) => {
  if (variant === "mobile") {
    return (
      <div className="flex justify-center py-1.5">
        <span className="h-1 w-10 rounded-full bg-gray-300" />
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 px-3 py-2">
      <span className="h-2 w-2 rounded-full bg-red-400/70" />
      <span className="h-2 w-2 rounded-full bg-amber-400/70" />
      <span className="h-2 w-2 rounded-full bg-green-400/70" />
    </div>
  );
};

const BannerSlot: React.FC<BannerSlotConfig> = ({
  id,
  label,
  ratioLabel,
  variant,
  galleryAspect,
  aspect,
  frameWidth,
  slot,
  hasExisting,
  campaignType,
  error,
}) => {
  const { preview, onFileChange: onChange, onRemove } = slot;

  const [imgFailed, setImgFailed] = React.useState(false);
  React.useEffect(() => setImgFailed(false), [preview]);
  const isBlob = typeof preview === "string" && preview.startsWith("blob:");
  const brokenExistingPreview =
    Boolean(preview) && !isBlob && !slot.galleryLabel && imgFailed;

  const showPreview = Boolean(preview) && !brokenExistingPreview;
  const showExisting = (hasExisting && !preview) || brokenExistingPreview;
  const showUpload = !hasExisting && !preview;

  return (
    <div className={`flex flex-col gap-3 ${frameWidth}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
            {VARIANT_ICON[variant]}
          </span>
          <Label htmlFor={id} className="font-medium">
            {label}
          </Label>
        </div>
        <span className="rounded-full border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
          {ratioLabel}
        </span>
      </div>

      <div className="overflow-hidden rounded-xl border bg-gradient-to-b from-gray-50 to-white shadow-sm">
        <FrameChrome variant={variant} />

        {showPreview && (
          <div className="relative">

            <div className={`relative ${aspect} w-full overflow-hidden`} style={CHECKERBOARD_STYLE}>
              <img
                src={preview as string}
                alt={`Preview ${label}`}
                className="h-full w-full object-contain"
                onError={() => setImgFailed(true)}
              />
            </div>
            <div className="absolute right-2 top-2 flex gap-2">
              <FileUploadButton
                inputId={id}
                accept={IMAGE_ACCEPT}
                onFileChange={onChange}
                variant="secondary"
                size="icon"
                className="pointer-events-auto h-11 w-11 shadow"
                title="Trocar imagem"
                aria-label={UPLOAD_COPY.replaceBanner(label)}
              >
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
              </FileUploadButton>
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="h-11 w-11 shadow"
                onClick={onRemove}
                title="Remover imagem"
                aria-label={`Remover ${label}`}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {showExisting && (
          <FileUploadButton
            inputId={id}
            accept={IMAGE_ACCEPT}
            onFileChange={onChange}
            variant="outline"
            className={`${EXISTING_CLASS} ${aspect}`}
            aria-label={UPLOAD_COPY.replaceExistingBanner(label)}
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-green-100 text-green-600">
              <CheckCircle className="h-6 w-6" aria-hidden="true" />
            </span>
            <span className="text-sm font-medium text-green-800">
              Banner já enviado
            </span>
            <span className="inline-flex items-center gap-1 text-xs font-medium text-primary opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
              <RefreshCw className="h-3 w-3" aria-hidden="true" />
              Clique para substituir
            </span>
          </FileUploadButton>
        )}

        {showUpload && (
          <FileUploadButton
            inputId={id}
            accept={IMAGE_ACCEPT}
            onFileChange={onChange}
            variant="outline"
            className={`${UPLOAD_CLASS} ${aspect}`}
            aria-label={UPLOAD_COPY.chooseBanner(label)}
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400 transition-colors group-hover:bg-primary/10 group-hover:text-primary">
              <UploadCloud className="h-6 w-6" aria-hidden="true" />
            </span>
            <span className="text-sm font-medium text-gray-600 group-hover:text-primary">
              Clique para fazer upload
            </span>
            <span className="text-xs text-gray-400">PNG, JPG ou WEBP · máx. {MAX_UPLOAD_MB}MB</span>
          </FileUploadButton>
        )}
      </div>

      <BannerGalleryPicker
        aspect={galleryAspect}
        slotLabel={label}
        campaignType={campaignType}
        selectedLabel={slot.galleryLabel}
        onSelect={slot.onGallerySelect}
      />

      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
};

export const BannersCard: React.FC<BannersCardProps> = ({
  errors,
  cover,
  alternative,
  hasBanner3x4,
  hasBanner16x9,
  campaignType,
}) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <ImageIcon className="h-5 w-5" />
          </span>
          <div>
            <CardTitle>Banners da Campanha</CardTitle>
            <p className="text-sm text-muted-foreground">
              Imagens exibidas para o cliente. Só a capa do card na home é obrigatória.
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap items-start gap-8">
          <BannerSlot
            id="banner3x4"
            label={`${CUSTOMER_FACING_COPY.coverLabel} *`}
            ratioLabel={CUSTOMER_FACING_COPY.coverRatio}
            variant="mobile"
            galleryAspect={BannerAspect.PORTRAIT}
            aspect="aspect-[3/4]"
            frameWidth="w-full max-w-[220px]"
            slot={cover}
            hasExisting={hasBanner3x4}
            campaignType={campaignType}
            error={
              errors.banner3x4
                ? String(errors.banner3x4.message || FILE_MESSAGES.banner3x4Image)
                : undefined
            }
          />

          <BannerSlot
            id="banner16x9"
            label={`${CUSTOMER_FACING_COPY.alternativeLabel} *`}
            ratioLabel={CUSTOMER_FACING_COPY.alternativeRatio}
            variant="desktop"
            galleryAspect={BannerAspect.WIDE}
            aspect="aspect-[16/9]"
            frameWidth="w-full max-w-[420px]"
            slot={alternative}
            hasExisting={hasBanner16x9}
            campaignType={campaignType}
            error={
              errors.banner16x9
                ? String(errors.banner16x9.message || FILE_MESSAGES.banner16x9Image)
                : undefined
            }
          />
        </div>

        <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
          <span aria-hidden="true">💡</span>
          <p className="text-xs text-blue-800">
            {CUSTOMER_FACING_COPY.bannersHint} Ao enviar uma nova imagem, a
            atual é substituída.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
